import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ChevronRight, FileText, X } from "lucide-react";
import { Avatar, Button, EmptyState, Pill, PageTitle } from "@/design/components";
import { formatHumanCurrency } from "@/design/format";
import { cn } from "@/design/cn";
import { useActiveProfile, useSeed, useActorId, useProjects } from "@/domain/hooks";
import { useStore } from "@/domain/store";
import { flushQueue } from "@/domain/offlineQueue";
import { useFileDrop } from "@/domain/useFileDrop";
import { api, demoModeActive } from "@/domain/api";
import { normalizeSpendRequest } from "@/domain/normalizeSpend";
import type { SpendRequest } from "@/domain/types";
import { useAuthStore } from "@/domain/authStore";

// A real file picked from the device. The object URL backs the thumbnail and is revoked
// when the item is removed or the screen unmounts, so we never leak blobs.
type TrayItem = { id: string; name: string; url: string; kind: "image" | "document" };

export function ReporterEvidenceIndex() {
  const seed = useSeed();
  const actorId = useActorId();
  const navigate = useNavigate();

  // Demo mode reads the seed store; live (API-first) mode loads the reporter's own
  // requests from the backend (my-requests is server-scoped to the signed-in user),
  // aggregated across the workspace projects — mirroring the Inbox.
  //
  // useProjects(), not seed.projects: seed is always the demo/fixture blob
  // (GET /api/workspace-seed, hydrated regardless of mode), whose project
  // ids are fake slugs a real getMySpendRequests(id) call would always
  // reject - this screen showed "nothing awaiting evidence" in real mode
  // even when real approved asks existed, for the same reason as Inbox.tsx.
  const demo = demoModeActive();
  const realProjects = useProjects();
  // Keyed on the ids, not the array object: this effect setStates, so an
  // array-identity dep would re-fire it on its own re-render — an unbounded
  // my-requests loop that froze this screen.
  const projectIdsKey = realProjects.map((project) => project.id).join(",");
  const [dbApproved, setDbApproved] = useState<SpendRequest[]>([]);
  useEffect(() => {
    if (demo) return;
    let cancelled = false;
    Promise.allSettled(realProjects.map((project) => api.getMySpendRequests(project.id)))
      .then((results) => {
        if (cancelled) return;
        const rows = results
          .filter((r): r is PromiseFulfilledResult<Record<string, unknown>[]> => r.status === "fulfilled")
          .flatMap((r) => r.value)
          .map(normalizeSpendRequest);
        setDbApproved(rows.filter((r) => r.state === "approved_for_spend" || r.state === "committed"));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- projectIdsKey stands in for realProjects
  }, [demo, projectIdsKey]);

  const approved = demo
    ? seed.spendRequests.filter((r) => r.reporterId === actorId && (r.state === "approved_for_spend" || r.state === "committed"))
    : dbApproved;

  const [tray, setTray] = useState<TrayItem[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Keep a live ref to the tray so the unmount cleanup sees the latest items without
  // re-running mid-session (which would revoke URLs still backing visible thumbnails).
  const trayRef = useRef(tray);
  trayRef.current = tray;

  // Revoke every remaining object URL on unmount so picked-file blobs are not leaked.
  // Per-item removal is handled in removeTrayItem; this only mops up on teardown.
  useEffect(() => () => trayRef.current.forEach((t) => URL.revokeObjectURL(t.url)), []);

  function addToTray(files: File[]) {
    if (files.length === 0) return;
    setTray((xs) => [
      ...xs,
      ...files.map((f) => ({
        id: `tray-${Math.random().toString(36).slice(2, 8)}`,
        name: f.name,
        url: URL.createObjectURL(f),
        kind: (f.type.startsWith("image/") ? "image" : "document") as TrayItem["kind"],
      })),
    ]);
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    addToTray(Array.from(e.target.files ?? []));
    // Allow re-picking the same file (e.g. re-take) by clearing the input value.
    e.target.value = "";
  }

  const fileDrop = useFileDrop({ onFiles: addToTray });

  function removeTrayItem(id: string) {
    setTray((xs) => {
      const gone = xs.find((t) => t.id === id);
      if (gone) URL.revokeObjectURL(gone.url);
      return xs.filter((t) => t.id !== id);
    });
  }

  return (
    <div className="p-4">
      <PageTitle title="Evidence" subtitle="Approved asks awaiting evidence." />
      {approved.length === 0 ? (
        <div className="mt-4">
          <EmptyState title="Nothing awaiting evidence" body="Once your contractor lead approves a spend ask, it appears here for you to evidence." />
        </div>
      ) : (
        <ul className="mt-4 space-y-2">
          {approved.map((r) => (
            <li key={r.id}>
              <button onClick={() => navigate(`/reporter/evidence/${r.id}`)} className="flex w-full items-center justify-between rounded-card border border-line bg-card p-3 text-left">
                <div>
                  <p className="text-sm font-medium text-ink">{r.title}</p>
                  <p className="text-xs text-faint">{formatHumanCurrency(r.amount)}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-faint" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Quick capture — a real file picker (camera on mobile) feeding an on-screen file tray, or drag and drop. */}
      <div
        onDragOver={fileDrop.onDragOver}
        onDragLeave={fileDrop.onDragLeave}
        onDrop={fileDrop.onDrop}
        className={cn(
          "mt-6 space-y-3 rounded-card border p-4 transition-colors",
          fileDrop.isDraggingOver ? "border-brand bg-brandSoft" : "border-line bg-card",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-ink">File tray</p>
          {tray.length > 0 && <span className="text-xs tabular-nums text-mute">{tray.length} captured</span>}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          multiple
          className="hidden"
          onChange={onPickFiles}
        />

        {tray.length === 0 ? (
          <p className="text-sm text-faint">Capture a photo or receipt now (or drag and drop one here); attach it to an ask above when you submit evidence.</p>
        ) : (
          <ul className="grid grid-cols-3 gap-2.5">
            {tray.map((t) => (
              <li key={t.id} className="relative">
                {t.kind === "image" ? (
                  <img src={t.url} alt={t.name} className="h-24 w-full rounded-control border border-line object-cover" />
                ) : (
                  <div className="flex h-24 w-full flex-col items-center justify-center gap-1.5 rounded-control border border-line bg-subtle px-2 text-center">
                    <FileText className="h-6 w-6 text-faint" aria-hidden />
                    <span className="line-clamp-2 break-all text-meta text-mute">{t.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeTrayItem(t.id)}
                  aria-label={`Remove ${t.name}`}
                  className="focus-ring absolute right-0 top-0 flex h-11 w-11 items-center justify-center rounded-full text-mute hover:text-ink"
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-card/90 shadow-overlay">
                    <X className="h-3.5 w-3.5" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button variant="secondary" className="w-full" onClick={() => fileInputRef.current?.click()}>
          <Camera className="h-4 w-4" /> Quick capture
        </Button>
      </div>
    </div>
  );
}

export function ReporterProfile() {
  const profile = useActiveProfile();
  const seed = useSeed();
  const person = seed.people.find((p) => p.id === profile.personId);
  const offline = useStore((s) => s.reporterOffline);
  const toggleOffline = useStore((s) => s.toggleReporterOffline);
  const wifiOnly = useStore((s) => s.syncOnlyOnWifi);
  const toggleWifiOnly = useStore((s) => s.toggleSyncOnlyOnWifi);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  // Turning the restriction OFF is an explicit "stop holding my queue back"
  // signal - attempt a sync immediately rather than leaving anything queued
  // to wait for the next natural trigger (reconnect, backgrounding the tab,
  // or a manual "Sync now"). Turning it ON needs no equivalent action:
  // shouldAutoSync() already governs future automatic attempts from here on.
  function handleToggleWifiOnly() {
    const wasOn = wifiOnly;
    toggleWifiOnly();
    if (wasOn) void flushQueue();
  }

  return (
    <div className="p-4">
      <div className="flex items-center gap-3">
        <Avatar initials={profile.avatarInitials} size={48} />
        <div>
          <p className="text-base font-semibold text-ink">{profile.displayName}</p>
          <p className="text-sm text-mute">{profile.role}</p>
        </div>
      </div>

      <dl className="mt-6 divide-y divide-hairline rounded-card border border-line bg-card">
        <Row label="Organisation" value={profile.organisation} />
        <Row label="Assigned project" value={seed.projects[0]?.name ?? "—"} />
        <Row label="Email" value={person?.email ?? `${profile.displayName.toLowerCase().replace(/\s+/g, ".")}@contractor.co.ug`} />
      </dl>

      <div className="mt-6 space-y-2">
        <div className="rounded-card border border-line bg-card px-4 py-3">
          {/* The switch pill itself is a deliberately small (24x40px)
              visual indicator - rather than stretching that shape into an
              oversized button, the whole row is the tap target (a real
              44px+ touch target) and the pill is a plain non-interactive
              span reflecting its state. */}
          <button
            type="button"
            onClick={handleToggleWifiOnly}
            className="-mx-4 -my-3 flex min-h-11 w-[calc(100%+2rem)] items-center justify-between px-4 py-3"
            aria-pressed={wifiOnly}
            aria-label="Sync only on Wi-Fi"
          >
            <span className="text-sm text-ink">Sync only on Wi-Fi</span>
            <span className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${wifiOnly ? "bg-brand" : "bg-line"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${wifiOnly ? "left-[18px]" : "left-0.5"}`} />
            </span>
          </button>
          <p className="mt-1.5 text-xs text-mute">
            {wifiOnly
              ? "Automatic sync is paused on mobile data — tap Sync now to send queued items immediately. On networks we can't identify (common on iPhone), we'll still sync automatically."
              : "Off — syncs automatically on any connection, including mobile data. Sync now always works too."}
          </p>
        </div>
        <Toggle label="Push notifications" defaultOn />
        <button
          type="button"
          onClick={toggleOffline}
          className="flex min-h-11 w-full items-center justify-between rounded-card border border-line bg-card px-4 py-3"
          aria-pressed={offline}
          aria-label="Simulate offline"
        >
          <span className="text-sm text-ink">Simulate offline</span>
          <span className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${offline ? "bg-brand" : "bg-line"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${offline ? "left-[18px]" : "left-0.5"}`} />
          </span>
        </button>
        {offline && <p className="text-xs text-warn">Queued. Submissions will sync when connection returns.</p>}
      </div>

      <button onClick={() => { logout(); navigate("/login", { replace: true }); }} className="mt-6 w-full text-center text-sm text-mute hover:text-ink">
        Sign out
      </button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <dt className="text-sm text-faint">{label}</dt>
      <dd className="text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}

function Toggle({ label, defaultOn }: { label: string; defaultOn?: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-card border border-line bg-card px-4 py-3">
      <span className="text-sm text-ink">{label}</span>
      <Pill variant={defaultOn ? "success" : "neutral"}>{defaultOn ? "On" : "Off"}</Pill>
    </div>
  );
}
